import { Size as ScreenSize } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import { RawButton, Translation, useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CasinoAllButton, CasinoFilter, CasinoSliderButtons } from '@swf/casino';
import type { SliderHandles, State as SliderState } from '@swf/components/Slider/Slider';
import StarsNav, { Variant as NavVariant } from '@swf/components/StarsNavigation/StarsNavigation';
import Logo from '@swf/core/assets/icons/logo.svg';
import VegasLogo from '@swf/core/assets/icons/vegas-races-logo.svg';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { RaceFilter } from '@swf/core/types/CasinoRaces';

import {
  TXTWCL_My_Races,
  TXTWCL_Next_Races,
  TXTWCL_Terms_and_Conditions
} from './RaceHeader.messages.json';
import styles from './RaceHeader.scss';

interface Props {
  logo: boolean;
  title?: string;
  helpButton?: string;
  helpTitle?: string;
  help: boolean;
  moreLink: string;
  showFilters?: boolean;
  active?: TranslationTag['id'];
  handleFilter?(filter: RaceFilter): void;
  sliderRef?: React.RefObject<SliderHandles>;
  sliderState?: SliderState;
  slide?: boolean;
}

const filters: RaceFilter[] = [
  {
    tag: TXTWCL_Next_Races,
    service: 'all',
    auth: true
  },
  {
    tag: TXTWCL_My_Races,
    service: 'user',
    auth: true
  }
];

const RaceHeader: React.FC<Props> = ({
  logo,
  title = '',
  helpButton,
  helpTitle,
  help,
  moreLink,
  showFilters,
  active,
  handleFilter,
  sliderRef,
  sliderState,
  slide
}) => {
  const info = logo || help;
  const { screenSize } = useScreenSize();
  const isSiteDE = useSelector(select.cms.isSiteDE);
  const { modal } = useDispatch<Dispatch>();
  const showMoreButton = !!moreLink && !showFilters;
  const showTitle = !logo && title && title.length;

  const handleHelp = useCallback(() => {
    modal.enqueue({
      path: '/casino/help',
      locationState: {
        buttonText: helpButton,
        title: helpTitle
      }
    });
  }, [helpButton, helpTitle, modal]);

  if (!info && !slide && !showMoreButton) {
    return null;
  }

  return (
    <header
      className={classNames(styles.legacy, styles.header, {
        [styles.row]: !showFilters
      })}
    >
      {!!logo && <div />}
      {info && (
        <div className={classNames(styles.container)}>
          {!!showTitle && <span className={styles.title}>{title}</span>}
          {logo &&
            (isSiteDE ? <VegasLogo className={styles.logo} /> : <Logo className={styles.logo} />)}
          {help && (
            <RawButton
              data-testid="terms-and-conditions-button-on-races-tab-mobile"
              size="small"
              variant="casino"
              className={classNames(styles.terms, showTitle && styles.showTitle)}
              onClick={() => handleHelp()}
            >
              {screenSize === ScreenSize.XXSmall ? (
                `T&C`
              ) : (
                <Translation {...TXTWCL_Terms_and_Conditions} />
              )}
            </RawButton>
          )}
        </div>
      )}
      {!logo && <div />}
      {!showFilters && !moreLink && !slide && <div />}
      {showFilters && (
        <StarsNav
          classes={{ container: classNames(styles.filters, { [styles.info]: info }) }}
          variant={NavVariant.Transparent}
        >
          {handleFilter &&
            filters.map((filter) => (
              <CasinoFilter
                active={filter.tag.id === active}
                key={filter.tag.id}
                onClick={() => handleFilter(filter)}
                translation={filter.tag}
              />
            ))}
        </StarsNav>
      )}
      {(slide || showMoreButton) && (
        <div className={styles.buttons}>
          {showMoreButton && <CasinoAllButton href={moreLink} />}
          {slide && sliderRef && sliderState && (
            <CasinoSliderButtons sliderRef={sliderRef} sliderState={sliderState} />
          )}
        </div>
      )}
    </header>
  );
};

export default RaceHeader;
